import { useTranslation } from '@pancakeswap/localization'
import { Box, Breadcrumbs, Button, Card, ErrorState, Flex, Heading, LoadingState, NextLinkFromReactRouter, Tag, Text } from '@pancakeswap/uikit'
import { useMemo } from 'react'
import styled from 'styled-components'
import { getBlockExploreLink } from 'utils'
import { DEX_LABEL, useInfoRoute, usePoolDetail } from 'state/infoHub/hooks'
import { Transaction, TransactionType } from 'state/info/types'
import ChartCard from 'views/Info/components/InfoCharts/ChartCard'
import TransactionTable from 'views/Info/components/InfoTables/TransactionsTable'
import Percent from 'views/Info/components/Percent'
import { DoubleCurrencyLogo } from 'views/Info/components/CurrencyLogo'
import type { InfoTransaction } from 'server/info/types'
import { DataStateBar } from './DataStateBar'
import { History } from './History'
import { feeTierLabel, num, shortAddress, usd } from './format'
import { pancakePoolsHref } from './pancakeLinks'

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`
const Tile = styled(Box)`
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  min-width: 0;
  overflow-wrap: anywhere;
`

export const toLegacyTransactions = (rows: InfoTransaction[]): Transaction[] =>
  rows.map((r) => ({
    type: r.type === 'swap' ? TransactionType.SWAP : r.type === 'mint' ? TransactionType.MINT : TransactionType.BURN,
    hash: r.hash,
    timestamp: String(r.timestamp),
    sender: r.sender,
    token0Symbol: r.token0.symbol,
    token1Symbol: r.token1.symbol,
    token0Address: r.token0.address,
    token1Address: r.token1.address,
    amountUSD: r.amountUSD ?? (undefined as unknown as number),
    amountToken0: r.amount0,
    amountToken1: r.amount1,
  }))

export const PoolDetailView: React.FC = () => {
  const { t } = useTranslation()
  const { dex, version, address, hrefFor } = useInfoRoute()
  const { detail, error, isLoading, retry, needsVersion } = usePoolDetail(dex, version, address)
  const transactions = useMemo(() => (detail?.transactions.data ? toLegacyTransactions(detail.transactions.data) : undefined), [detail])
  if (!dex || !address) return null
  if (needsVersion) {
    return (
      <ErrorState
        title={t('Choose a version')}
        description={t('A pool belongs to one version. Open it from the V2 or V3 list.')}
        action={
          <Flex style={{ gap: 8 }}>
            <Button as={NextLinkFromReactRouter} to={`/info/${dex}/pools/${address}?v=v2`} scale="sm">
              V2
            </Button>
            <Button as={NextLinkFromReactRouter} to={`/info/${dex}/pools/${address}?v=v3`} scale="sm">
              V3
            </Button>
          </Flex>
        }
        tone="warning"
      />
    )
  }
  const pool = detail?.pool.data
  return (
    <Box>
      <Breadcrumbs mb="16px">
        <NextLinkFromReactRouter to="/info">{t('Info')}</NextLinkFromReactRouter>
        {dex === 'pancakeswap' ? (
          // no PancakeSwap tab any more: back to the PancakeSwap pools of the pool's CADINU / CBON side (pancakeLinks.ts)
          pool &&
          (pancakePoolsHref(pool.token0.address) ?? pancakePoolsHref(pool.token1.address)) && (
            <NextLinkFromReactRouter to={(pancakePoolsHref(pool.token0.address) ?? pancakePoolsHref(pool.token1.address)) as string}>
              {t('%symbol% pools on PancakeSwap', { symbol: pancakePoolsHref(pool.token0.address) ? pool.token0.symbol : pool.token1.symbol })}
            </NextLinkFromReactRouter>
          )
        ) : (
          <NextLinkFromReactRouter to={hrefFor(dex, version, 'pools')}>
            {DEX_LABEL[dex]} {t('pools')}
          </NextLinkFromReactRouter>
        )}
        <Text>{pool ? `${pool.token0.symbol}/${pool.token1.symbol}` : shortAddress(address)}</Text>
      </Breadcrumbs>
      {isLoading && !detail && <LoadingState rows={4} label={t('Loading pool')} />}
      {error && !detail && <ErrorState title={t('Could not load pool')} description={error.message} onRetry={retry} retryLabel={t('Retry')} />}
      {detail && !pool && <ErrorState title={t('Pool not found in this DEX/version')} description={detail.pool.meta.error} onRetry={retry} retryLabel={t('Retry')} tone="warning" />}
      {pool && (
        <>
          <Flex alignItems="center" flexWrap="wrap" style={{ gap: 8 }} mb="8px">
            <DoubleCurrencyLogo address0={pool.token0.address} address1={pool.token1.address} size={28} />
            <Heading scale="lg" as="h1">
              {pool.token0.symbol}/{pool.token1.symbol}
            </Heading>
            <Tag variant="secondary" outline>
              {DEX_LABEL[pool.dex]} {pool.version.toUpperCase()} {pool.feeTier !== undefined ? feeTierLabel(pool.feeTier) : ''}
            </Tag>
            <Button as="a" href={getBlockExploreLink(pool.address, 'address', pool.chainId)} target="_blank" rel="noreferrer" scale="sm" variant="text">
              {shortAddress(pool.address)} ↗
            </Button>
            <Flex ml="auto" style={{ gap: 8 }}>
              <Button as={NextLinkFromReactRouter} to={`/${pool.version === 'v3' ? 'add' : 'v2/add'}/${pool.token0.address}/${pool.token1.address}${pool.version === 'v3' && pool.feeTier !== undefined ? `/${pool.feeTier}` : ''}`} scale="sm" variant="secondary" data-testid="ih-add">
                {t('Add liquidity')}
              </Button>
              <Button as={NextLinkFromReactRouter} to={`/swap?inputCurrency=${pool.token0.address}&outputCurrency=${pool.token1.address}`} scale="sm" data-testid="ih-trade">
                {t('Trade')}
              </Button>
            </Flex>
          </Flex>
          <Text fontSize="13px" color="textSubtle" mb="12px">
            1 {pool.token0.symbol} = {num(pool.token0Price)} {pool.token1.symbol} · 1 {pool.token1.symbol} = {num(pool.token1Price)} {pool.token0.symbol} · {t('LP fee')} {(pool.lpFee * 100).toFixed(2)}%
          </Text>
          <DataStateBar meta={detail!.pool.meta} onRetry={retry} />
          <Box mt="12px">
            <Grid>
              <Tile>
                <Text fontSize="12px" color="textSubtle" bold textTransform="uppercase">
                  {t('TVL')}
                </Text>
                <Text fontSize="20px" bold color={pool.tvlUSD === null ? 'failure' : 'text'}>
                  {usd(pool.tvlUSD)}
                </Text>
                <Text fontSize="12px" color="textSubtle">
                  {num(pool.reserve0)} {pool.token0.symbol} + {num(pool.reserve1)} {pool.token1.symbol}
                </Text>
                <Text fontSize="12px" color="textSubtle">
                  {pool.tvlMethod}
                </Text>
              </Tile>
              <Tile>
                <Text fontSize="12px" color="textSubtle" bold textTransform="uppercase">
                  {t('Volume 24H')}
                </Text>
                <Text fontSize="20px" bold color={pool.volume24hUSD === null ? 'failure' : 'text'}>
                  {usd(pool.volume24hUSD)} <Percent value={pool.volume24hChange ?? undefined} fontSize="12px" />
                </Text>
                <Text fontSize="12px" color="textSubtle">
                  {num(pool.volume24hToken0)} {pool.token0.symbol} · {num(pool.volume24hToken1)} {pool.token1.symbol} · {pool.txCount24h} {t('swaps')}
                </Text>
                <Text fontSize="12px" color="textSubtle">
                  {pool.volumeMethod}
                </Text>
              </Tile>
              <Tile>
                <Text fontSize="12px" color="textSubtle" bold textTransform="uppercase">
                  {t('Volume 7D')}
                </Text>
                <Text fontSize="20px" bold color={pool.volume7dUSD === null ? 'failure' : 'text'}>
                  {usd(pool.volume7dUSD)} <Percent value={pool.volume7dChange ?? undefined} fontSize="12px" />
                </Text>
                <Text fontSize="12px" color="textSubtle">
                  {pool.txCount7d} {t('swaps')}
                </Text>
              </Tile>
              <Tile>
                <Text fontSize="12px" color="textSubtle" bold textTransform="uppercase">
                  {pool.version === 'v3' ? t('Active liquidity') : t('LP fees 24H')}
                </Text>
                {pool.version === 'v3' ? (
                  <>
                    <Text fontSize="20px" bold color={pool.activeLiquidity === '0' ? 'warning' : 'text'}>
                      {pool.activeLiquidity === '0' ? t('none in range') : t('in range')}
                    </Text>
                    <Text fontSize="12px" color="textSubtle" title={pool.activeLiquidity}>
                      liquidity() = {pool.activeLiquidity} · tick {pool.tick} · {t('TVL above counts all positions, in and out of range')}
                    </Text>
                  </>
                ) : (
                  <Text fontSize="20px" bold>
                    {pool.volume24hUSD === null ? '—' : usd(pool.volume24hUSD * pool.lpFee)}
                  </Text>
                )}
              </Tile>
            </Grid>
          </Box>
          <Box mt="24px">
            {dex === 'cadinu' ? (
              <History scope="pool" id={pool.address} version={pool.version} canPickProtocol={false} />
            ) : (
              <>
                {detail!.chart.data && detail!.chart.data.length > 0 ? (
                  <>
                    <ChartCard variant="pool" chartData={detail!.chart.data} />
                    <Text fontSize="12px" color="textSubtle" mt="4px">
                      {t('History')}: {detail!.chart.meta.coverage}
                    </Text>
                  </>
                ) : (
                  <Card>
                    <Box p="16px">
                      <Heading scale="md" mb="8px">
                        {t('History')}
                      </Heading>
                      <DataStateBar meta={detail!.chart.meta} compact />
                    </Box>
                  </Card>
                )}

              </>
            )}
          </Box>
          <Heading scale="lg" mt="32px" mb="12px">
            {t('Transactions')}
          </Heading>
          {transactions ? <TransactionTable transactions={transactions} /> : <DataStateBar meta={detail!.transactions.meta} compact />}
        </>
      )}
    </Box>
  )
}
export default PoolDetailView
